import React, { useState, useEffect, useRef, useCallback } from "react";
import { SearchResultChalet } from "./SearchResultChalet";
import type { SearchQuery } from "@/types/searchquery";
import type { ChaletType, ResortInspireMeAttribute } from "@/types/chalet";
import ChaletLoader from "../ChaletLoader";

// import { Chalet } from "@mui/icons-material";
import ChaletSearchResultRefiners from "./ChaletSearchResultRefiners";
import type { CheckboxItem, SliderMark, ChaletItem } from "@/types/refiners";

import type { FilterCriteria } from "@/types/filters";

import { SliderSettings, CheckboxListSettings } from "@/types/refiners";
import { Resort } from "@/types/chaletData";

import ChaletSearchResultsIntroText from "./ChaletSearchResultsIntroText";

const apiUrl = process.env.NEXT_PUBLIC_REACT_APP_API_URL;

interface ChaletSearchResultsProps {
  updateSearchCount?: (count: number) => void;
  SearchQuery?: SearchQuery | null;
  inspireMeAttributeId?: number;
  pageHeadertext?: string;
}

const ChaletSearchResults: React.FC<ChaletSearchResultsProps> = ({
  updateSearchCount,
  SearchQuery,
  inspireMeAttributeId,
  pageHeadertext,
}) => {
  const [isSearchInProgress, setIsSearchInProgress] = useState(false);

  const [isSearchInProgressLoader, setisSearchInProgressLoader] =
    useState(true);

  const chaletListRef = useRef(null);
  const [allChalets, setAllChalets] = useState<ChaletType[]>([]);
  const [displayChalets, setDisplayChalets] = useState<ChaletType[]>([]);

  const getResortId = (searchQueryObject: SearchQuery) => {
    return searchQueryObject.ResortId !== undefined
      ? searchQueryObject.ResortId
      : searchQueryObject.ResortId;
  };

  const loadChaletSettings = useCallback((fetchedChalets: ChaletType[]) => {
    setisSearchInProgressLoader(false);

    setAllChalets((prevChalets) => {
      // Combine, deduplicate, and sort chalets by price (ascending)
      const updatedChalets = Array.from(
        new Map(
          [...prevChalets, ...fetchedChalets].map((chalet) => [
            chalet.id,
            chalet,
          ])
        ).values()
      ).sort((a, b) => a.total - b.total);

      // Recalculate ranges for sliders
      const lowestPrice = updatedChalets.reduce(
        (min, chalet) => Math.min(min, chalet.total),
        Infinity
      );
      const highestPrice = updatedChalets.reduce(
        (max, chalet) => Math.max(max, chalet.total),
        0
      );

      const lowestDistanceToResortCentre = updatedChalets.reduce(
        (min, chalet) => Math.min(min, chalet.distanceFromResortCentreMeters),
        Infinity
      );
      const highestDistanceToResortCentre = updatedChalets.reduce(
        (max, chalet) => Math.max(max, chalet.distanceFromResortCentreMeters),
        0
      );

      const lowestDistanceToSkiLift = updatedChalets.reduce(
        (min, chalet) => Math.min(min, chalet.distanceFromLiftMeters),
        Infinity
      );
      const highestDistanceToSkiLift = updatedChalets.reduce(
        (max, chalet) => Math.max(max, chalet.distanceFromLiftMeters),
        0
      );

      // Update price range
      const newPriceRange: [number, number] = [lowestPrice, highestPrice];
      setDefaultPriceRange(newPriceRange);
      setPriceRange(newPriceRange);

      // Update resort centre distance range
      const newResortRange: [number, number] = [
        lowestDistanceToResortCentre,
        highestDistanceToResortCentre,
      ];
      setResortCentreRange(newResortRange);
      setDefaultResortCentreRange(newResortRange);

      // Update ski lift distance range
      const newLiftRange: [number, number] = [
        lowestDistanceToSkiLift,
        highestDistanceToSkiLift,
      ];
      setDefaultSkiLiftRange(newLiftRange);
      setSkiLiftRange(newLiftRange);

      return updatedChalets;
    });

    // Update display list and count
    setDisplayChalets((prev) => {
      return Array.from(
        new Map(
          [...prev, ...fetchedChalets].map((chalet) => [chalet.id, chalet])
        ).values()
      ).sort((a, b) => a.total - b.total);
    });
  }, []);

  const executeSearchByInspirationAttribute = useCallback(
    (inspireMeAttributeId: number) => {
      if (!inspireMeAttributeId) return;

      fetch(`${apiUrl}/SearchByInspireMeAttribute/${inspireMeAttributeId}`)
        .then((response) => response.json())
        .then((data) => {
          if (data?.properties?.length > 0) {
            const fetchedChalets: ChaletType[] = data.properties.map(
              (chalet: ChaletType) => ({
                id: chalet.chaletId,
                name: chalet.name,
                image: chalet.thumbnailImageUrl,
                price: chalet.totalQueryCost,
                total: chalet.totalQueryCost,
                currencySymbol: chalet.currencySymbol,
                country: chalet.countryName,
                resort: chalet.resort.name,
                resortID: chalet.resort.id,
                resortLat: chalet.resort.lat,
                resortLng: chalet.resort.lng,
                link: chalet.internalUrl,
                distanceFromResortCentreMeters:
                  chalet.distanceFromResortCentreMeters,
                distanceFromLiftMeters: chalet.distanceFromLiftMeters,
                reviewScore: chalet.reviewScore,
                lat: chalet.lat,
                lng: chalet.lng,
                accommodationTypeName: chalet.accommodationTypeName,
                resortInspireMeAttributes:
                  data.resortInspireMeAttributes.filter(
                    (attr: ResortInspireMeAttribute) =>
                      attr.resort.id === chalet.resort.id
                  ),
                totalStayFormatted: `${
                  chalet.currencySymbol
                }${chalet.totalQueryCost.toLocaleString("en-GB", {
                  style: "decimal",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })} for total stay`,
              })
            );

            // Update Resort List
            setResortList((prev) => {
              const resortUpdates = fetchedChalets.map(
                ({ resort, resortID }) => ({
                  name: resort,
                  id: resortID,
                })
              );

              const merged = [...prev, ...resortUpdates];
              const deduplicated = Array.from(
                new Map(merged.map((r) => [r.id, r])).values()
              ).filter((r): r is ChaletItem => r !== undefined);

              return deduplicated;
            });

            // Update Inspire Me List
            setInspireMeAttributeList((prev) => {
              const updates = data.resortInspireMeAttributes.map(
                (attr: ResortInspireMeAttribute) => ({
                  resortId: attr.resort.id,
                  resortName: attr.resort.name,
                  attributeId: attr.inspireMeAttribute.id,
                  attributeName: attr.inspireMeAttribute.attributeName,
                  description: attr.inspireMeAttribute.description,
                })
              );

              const existingIds = new Set(prev.map((a: ChaletItem) => a.id));
              const unique = updates.filter(
                (a: ChaletItem) => !existingIds.has(a.id)
              );

              return [...prev, ...unique];
            });

            // Update Accommodation Types
            setAccommodationTypeList((prev) => {
              const updates = data.accommodationTypeNameList.map(
                (name: string) => ({
                  accommodationTypeName: name,
                })
              );

              const existing = new Set(
                prev.map((a) => a.accommodationTypeName)
              );
              const unique = updates.filter(
                (a: CheckboxItem) => !existing.has(a.accommodationTypeName)
              );

              return [...prev, ...unique];
            });

            // Update Price Marks
            setPriceRangeMarks((prev) =>
              getThreePointMarks([...prev, ...data.priceRefinerMarks])
            );

            // Update Resort Centre Distance Marks
            setResortCentreRangeMarks((prev) =>
              getThreePointMarks(
                [...prev, ...data.distancetoCentreRefinerMarks],
                "m"
              )
            );

            // Update Ski Lift Distance Marks
            setSkiLitRangeMarks((prev) =>
              getThreePointMarks(
                [...prev, ...data.distancetoSkiLiftRefinerMarks],
                "m"
              )
            );

            // Load Chalets
            loadChaletSettings(fetchedChalets);
          }
        })
        .catch((error) => {
          console.error("Fetch inspiration properties failed:", error);
        });
    },
    [loadChaletSettings]
  );

  const executeSearchIndividualResort = useCallback(
    (SearchQueryObject: SearchQuery) => {
      // Handle Country Search Array
      if (
        SearchQueryObject.CountrySearchArray !== undefined &&
        !Array.isArray(SearchQueryObject.CountrySearchArray)
      ) {
        SearchQueryObject.CountrySearchArray = [
          SearchQueryObject.CountrySearchArray,
        ];
      }

      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(SearchQueryObject),
      };

      fetch(apiUrl + "/SearchByResort", requestOptions)
        .then((response) => response.json())
        .then((data) => {
          if (data?.properties?.length > 0) {
            const fetchedChalets: ChaletType[] = data.properties.map(
              (chalet: ChaletType) => ({
                id: chalet.chaletId,
                name: chalet.name,
                image: chalet.thumbnailImageUrl,
                price: chalet.totalQueryCost,
                total: chalet.totalQueryCost,
                currencySymbol: chalet.currencySymbol,
                country: chalet.countryName,
                resort: chalet.resort.name,
                resortID: chalet.resort.id,
                resortLat: chalet.resort.lat,
                resortLng: chalet.resort.lng,
                link: chalet.internalUrl,
                distanceFromResortCentreMeters:
                  chalet.distanceFromResortCentreMeters,
                distanceFromLiftMeters: chalet.distanceFromLiftMeters,
                reviewScore: chalet.reviewScore,
                lat: chalet.lat,
                lng: chalet.lng,
                accommodationTypeName: chalet.accommodationTypeName,
                resortInspireMeAttributes:
                  data.resortInspireMeAttributes.filter(
                    (attr: ResortInspireMeAttribute) =>
                      attr.resort.id === chalet.resort.id
                  ),
                totalStayFormatted: `${
                  chalet.currencySymbol
                }${chalet.totalQueryCost.toLocaleString("en-GB", {
                  style: "decimal",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })} for total stay`,
              })
            );

            // Update Resort List
            setResortList((prev) => {
              const resortUpdates = fetchedChalets.map(
                ({ resort, resortID }) => ({
                  name: resort,
                  id: resortID,
                })
              );

              const merged = [...prev, ...resortUpdates];
              const deduplicated = Array.from(
                new Map(merged.map((r) => [r.id, r])).values()
              ).filter((r): r is ChaletItem => r !== undefined);

              return deduplicated;
            });

            // Update Inspire Me List
            setInspireMeAttributeList((prev) => {
              const updates = data.resortInspireMeAttributes.map(
                (attr: ResortInspireMeAttribute) => ({
                  resortId: attr.resort.id,
                  resortName: attr.resort.name,
                  attributeId: attr.inspireMeAttribute.id,
                  attributeName: attr.inspireMeAttribute.attributeName,
                  description: attr.inspireMeAttribute.description,
                })
              );

              const existingIds = new Set(prev.map((a: ChaletItem) => a.id));
              const unique = updates.filter(
                (a: ChaletItem) => !existingIds.has(a.id)
              );

              return [...prev, ...unique];
            });

            // Update Accommodation Types
            setAccommodationTypeList((prev) => {
              const updates = data.accommodationTypeNameList.map(
                (name: string) => ({
                  accommodationTypeName: name,
                })
              );

              const existing = new Set(
                prev.map((a) => a.accommodationTypeName)
              );
              const unique = updates.filter(
                (a: CheckboxItem) => !existing.has(a.accommodationTypeName)
              );

              return [...prev, ...unique];
            });

            // Update Price Marks
            setPriceRangeMarks((prev) =>
              getThreePointMarks([...prev, ...data.priceRefinerMarks])
            );

            // Update Resort Centre Distance Marks
            setResortCentreRangeMarks((prev) =>
              getThreePointMarks(
                [...prev, ...data.distancetoCentreRefinerMarks],
                "m"
              )
            );

            // Update Ski Lift Distance Marks
            setSkiLitRangeMarks((prev) =>
              getThreePointMarks(
                [...prev, ...data.distancetoSkiLiftRefinerMarks],
                "m"
              )
            );

            // Load Chalets
            loadChaletSettings(fetchedChalets);
          }
        })
        .catch((error) => {
          console.error("Fetch resort properties failed:", error);
        });
    },
    [loadChaletSettings]
  );

  const executeSearch = useCallback(
    (SearchQueryObject: SearchQuery) => {
      if (isSearchInProgress) return;

      setIsSearchInProgress(true);

      // Check for InspireMeAttributeId first
      if (inspireMeAttributeId) {
        executeSearchByInspirationAttribute(inspireMeAttributeId);
        return;
      }
      const countrySearchArray: number[] =
        Array.isArray(SearchQueryObject.CountrySearchArray) &&
        SearchQueryObject.CountrySearchArray.length > 0
          ? SearchQueryObject.CountrySearchArray
          : [];

      if (
        SearchQueryObject.ResortIds &&
        SearchQueryObject.ResortIds.length > 0
      ) {
        SearchQueryObject.ResortIds.forEach((resortId, index) => {
          setTimeout(() => {
            executeSearchIndividualResort({
              ...SearchQueryObject,
              ResortId: resortId,
            });
          }, index * 500);
        });
        return;
      }

      const resortId = getResortId(SearchQueryObject);

      if (
        countrySearchArray.length === 0 &&
        resortId !== 0 &&
        resortId !== undefined
      ) {
        executeSearchIndividualResort(SearchQueryObject);
      } else if (countrySearchArray.length > 0) {
        const requestOptions = {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...SearchQueryObject,
            CountrySearchArray: countrySearchArray,
          }),
        };

        fetch(apiUrl + "/SearchGetResortsByCountry", requestOptions)
          .then((response) => response.json())
          .then((data) => {
            if (data?.resorts?.length > 0) {
              data.resorts
                .slice(0, 5)
                .forEach((resort: Resort, index: number) => {
                  setTimeout(() => {
                    executeSearchIndividualResort({
                      ...SearchQueryObject,
                      ResortId: resort.id,
                    });
                  }, index * 500);
                });
            }
          })
          .catch((error) => {
            console.error("Country resort fetch failed:", error);
          });
      }
    },
    [
      isSearchInProgress,
      executeSearchIndividualResort,
      executeSearchByInspirationAttribute,
      inspireMeAttributeId,
    ]
  );

  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>({
    minPrice: 0,
    maxPrice: Infinity,
    minResortDistance: 0,
    maxResortDistance: Infinity,
    minLiftDistance: 0,
    maxLiftDistance: Infinity,
    minReviewScore: 0,
    maxReviewScore: 10,
    selectedResorts: [],
    selectedInspireMeAttributes: [],
    selectedAccommodationTypes: [],
  });

  useEffect(() => {
    if (SearchQuery) {
      executeSearch(SearchQuery);
    }
  }, [SearchQuery, executeSearch]);

  useEffect(() => {
    // console.log("troublesome useffect firing");
    const filteredChalets = allChalets.filter((chalet: ChaletType) => {
      const resortAttributes = chalet.resortInspireMeAttributes || [];
      const hasSelectedAttributes =
        filterCriteria.selectedInspireMeAttributes.every((attr) =>
          resortAttributes.some(
            (resortAttr) => resortAttr.inspireMeAttribute.id === attr
          )
        );

      // Check if the chalet's accommodation type matches one of the selected types
      const hasSelectedAccommodationType =
        filterCriteria.selectedAccommodationTypes.length === 0 ||
        filterCriteria.selectedAccommodationTypes.includes(
          chalet.accommodationTypeName
        );

      return (
        Number(chalet.total) >= filterCriteria.minPrice &&
        Number(chalet.total) <= filterCriteria.maxPrice &&
        Number(chalet.distanceFromResortCentreMeters) >=
          filterCriteria.minResortDistance &&
        Number(chalet.distanceFromResortCentreMeters) <=
          filterCriteria.maxResortDistance &&
        Number(chalet.distanceFromLiftMeters) >=
          filterCriteria.minLiftDistance &&
        Number(chalet.distanceFromLiftMeters) <=
          filterCriteria.maxLiftDistance &&
        Number(chalet.reviewScore) >= filterCriteria.minReviewScore &&
        Number(chalet.reviewScore) <= filterCriteria.maxReviewScore &&
        (filterCriteria.selectedResorts.length === 0 ||
          filterCriteria.selectedResorts.includes(chalet.resortID)) &&
        (filterCriteria.selectedInspireMeAttributes.length === 0 ||
          hasSelectedAttributes) &&
        hasSelectedAccommodationType
      ); // Ensure it matches one of the selected types
    });
    setDisplayChalets(filteredChalets);
  }, [filterCriteria, allChalets]);

  const updateFilterCriteria = useCallback(
    (criteria: Partial<FilterCriteria>) => {
      setFilterCriteria((prev) => ({ ...prev, ...criteria }));
    },
    []
  );

  // Chalet Resort CheckList Code
  const [resortList, setResortList] = useState<ChaletItem[]>([]);

  const onResortCheckBoxChange = useCallback(
    (selectedResortIds: (number | string)[]) => {
      const selectedResortIdsInt = selectedResortIds.map((id) =>
        parseInt(id as string, 10)
      );
      updateFilterCriteria({ selectedResorts: selectedResortIdsInt });
    },
    [updateFilterCriteria]
  );

  const resortCheckListSettings = {
    title: "Resorts",
    onSelectionChange: onResortCheckBoxChange,
    items: resortList,
  };

  /// Inspire Me Attribute Checkbox List Code
  const [inspireMeAttributeList, setInspireMeAttributeList] = useState<
    ChaletItem[]
  >([]);

  const onInspireMeAttributeCheckBoxChange = (
    selectedIds: (number | string)[]
  ) => {
    const selectedInspireMeAttributeIdsInt = selectedIds.map((id) =>
      parseInt(id as string, 10)
    );

    updateFilterCriteria({
      selectedInspireMeAttributes: selectedInspireMeAttributeIdsInt,
    });
  };

  const inspireMeAttributeCheckboxListSettings: CheckboxListSettings = {
    title: "Inspire Me Categories",
    onSelectionChange: onInspireMeAttributeCheckBoxChange,
    items: inspireMeAttributeList,
  };

  // Accommodation Type Checkbox List Code
  const [accommodationTypeList, setAccommodationTypeList] = useState<
    ChaletItem[]
  >([]);

  const onAccommodationTypeCheckBoxChange = (selected: (string | number)[]) => {
    const selectedNames = selected.map((s) => String(s));
    updateFilterCriteria({ selectedAccommodationTypes: selectedNames });
  };

  const accommodationTypeCheckboxListSettings = {
    title: "Accommodation Type",
    onSelectionChange: onAccommodationTypeCheckBoxChange,
    items: accommodationTypeList,
  };

  //PriceRefiner Code
  const [defaultPriceRange, setDefaultPriceRange] = useState([0, 50000]); // Default price range
  const [priceRange, setPriceRange] = useState([0, 5000]); // Default price range
  const [priceRangeMarks, setPriceRangeMarks] = useState<SliderMark[]>([]);

  // Handler for price range changes
  const onPriceSliderChange = (newValue: [number, number]) => {
    const [minPrice, maxPrice] = newValue;
    updateFilterCriteria({ minPrice, maxPrice });
  };

  // Define the price slider settings with the handler function
  const priceSliderSettings: SliderSettings = {
    title: "Total Price",
    min: priceRange[0],
    max: priceRange[1],
    step: 50,
    defaultValue: (defaultPriceRange ?? [0, 5000]) as [number, number],
    onChange: onPriceSliderChange,
    marks: priceRangeMarks,
  };

  //Resort Centre Code
  const [defaultResortCentreRange, setDefaultResortCentreRange] = useState<
    [number, number] | null
  >(null);
  const [ResortCentreRange, setResortCentreRange] = useState([0, 50000]); // Default price range
  const [ResortCentreRangeMarks, setResortCentreRangeMarks] = useState<
    SliderMark[]
  >([]);

  // Handler for Resort Centre Distance range changes
  const onResortDistanceSliderChange = (newValue: [number, number]) => {
    const [minResortDistance, maxResortDistance] = newValue;
    updateFilterCriteria({ minResortDistance, maxResortDistance });
  };

  // Define the price slider settings with the handler function
  const distanceResortCentreSliderSettings: SliderSettings = {
    title: "Distance to Resort Centre",
    min: ResortCentreRange[0],
    max: ResortCentreRange[1],
    step: 50,
    defaultValue: (defaultResortCentreRange ?? [0, 500000]) as [number, number],
    onChange: onResortDistanceSliderChange,
    marks: ResortCentreRangeMarks,
  };

  //Ski Lift Distance Code
  const [defaultSkiLiftRange, setDefaultSkiLiftRange] = useState([0, 50000]); // Def
  const [SkiLiftRange, setSkiLiftRange] = useState([0, 50000]); // Default price range
  const [SkiLitRangeMarks, setSkiLitRangeMarks] = useState<SliderMark[]>([]);

  // Handler for Resort Centre Distance range changes
  const onLiftDistanceSliderChange = (newValue: [number, number]) => {
    const [minLiftDistance, maxLiftDistance] = newValue;
    updateFilterCriteria({ minLiftDistance, maxLiftDistance });
  };

  // Define the price slider settings with the handler function
  const distanceSkiLiftliderSettings: SliderSettings = {
    title: "Distance to Closest Ski Lift",
    min: SkiLiftRange[0],
    max: SkiLiftRange[1],
    step: 50,
    defaultValue: (defaultSkiLiftRange ?? [0, 5000]) as [number, number],
    onChange: onLiftDistanceSliderChange,
    marks: SkiLitRangeMarks,
  };

  const onRatingCompoentChange = (newValue: number) => {
    updateFilterCriteria({ minReviewScore: 0, maxReviewScore: newValue });
  };

  const propertyRatingSettings = {
    name: "Chalet Ratings",
    defaultValue: 10,
    onChange: onRatingCompoentChange,
  };

  useEffect(() => {
    if (updateSearchCount) {
      // Use a microtask to defer the state update
      Promise.resolve().then(() => updateSearchCount(displayChalets.length));
    }
  }, [displayChalets, updateSearchCount]);

  function getThreePointMarks(
    marks: { value: number; label?: string }[],
    unit: string = ""
  ) {
    const sorted = [...marks].sort((a, b) => a.value - b.value);
    const deduped = sorted.filter(
      (mark, i, self) => i === self.findIndex((m) => m.value === mark.value)
    );

    const lowest = deduped[0];
    const highest = deduped.at(-1)!;
    const midValue = (lowest.value + highest.value) / 2;

    const middle = deduped.reduce((closest, mark) =>
      Math.abs(mark.value - midValue) < Math.abs(closest.value - midValue)
        ? mark
        : closest
    );

    return [
      { value: lowest.value, label: `${lowest.value}${unit}` },
      { value: middle.value, label: `${middle.value}${unit}` },
      { value: highest.value, label: `${highest.value}${unit}` },
    ];
  }

  return (
    <div>
      <section ref={chaletListRef}>
        <div className="row">
          <div className="col-12 col-lg-3 col-xl-2"></div>
          <div className="col-12 col-lg-9 col-xl-10">
            <div className="bg-light">
              {typeof inspireMeAttributeId === "number" &&
              inspireMeAttributeId > 0 ? (
                <ChaletSearchResultsIntroText
                  inspireMeAttributeId={inspireMeAttributeId}
                />
              ) : (
                <div className="row">
                  <div className="col-12 col-lg-9 col-xl-10 offset-lg-3 offset-xl-2 p-4">
                    <section className="text-center py-5 text-white position-relative overflow-hidden">
                      <h1>
                        {pageHeadertext && pageHeadertext.trim() !== ""
                          ? pageHeadertext
                          : "Explore our chalet search results below"}
                      </h1>
                    </section>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-lg-9 col-xl-10 offset-lg-3 offset-xl-2 p-4 "></div>
        </div>
        <div className="row">
          <div className="col-12 col-lg-3 col-xl-2">
            <div className="sidebar-box">
              <ChaletSearchResultRefiners
                priceSliderSettings={priceSliderSettings}
                distanceResortCentreSliderSettings={
                  distanceResortCentreSliderSettings
                }
                distanceSkiLiftliderSettings={distanceSkiLiftliderSettings}
                propertyRatingSettings={propertyRatingSettings}
                resortCheckListSettings={resortCheckListSettings}
                inspireMeAttributeCheckboxListSettings={
                  inspireMeAttributeCheckboxListSettings
                }
                accommodationTypeCheckboxListSettings={
                  accommodationTypeCheckboxListSettings
                }
              />
            </div>
          </div>

          {displayChalets.length > 0 ? (
            <div
              className="col-12 col-lg-9 col-xl-10"
              key={displayChalets.length}
            >
              <div className="row" id="ChaletDataRow">
                {displayChalets.map((item) => (
                  <SearchResultChalet item={item} key={item.id} />
                ))}
              </div>
            </div>
          ) : (
            <div className="col-12 col-lg-8 col-xl-8">
              <div className="row">
                {isSearchInProgressLoader ? (
                  <div className="col-12 text-center">
                    <p>Hold tight... We’re scouring the Alps for you 🏔️</p>
                    <ChaletLoader />
                  </div>
                ) : (
                  <div className="col-12 text-center">
                    <p>
                      No chalets match your search. Try adjusting your filters.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ChaletSearchResults;
